import {Navigate, Outlet, useLocation} from 'react-router-dom';
import {useAuth} from 'src/components/AuthProvider.tsx';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import Layout from 'src/pages/Layout';
import {useLicenseQuota} from 'src/hooks/useLicenseQuota';
import {getHomePath} from 'src/utils/contextPreferences';

export default function ProtectedRoute() {
  const {token} = useAuth();
  const account = useAppSelector(selectAccount);
  const location = useLocation();
  const {isAtLimit} = useLicenseQuota();

  if (!token) {
    return <Navigate to="/login" />;
  }

  // Define restricted paths for Org Operators
  const restrictedPaths = [
    '/devices',
    '/property',
    '/tenant',
    '/visitors',
    '/community',
    '/settings',
    '/billing',
  ];

  // Check if current path is restricted
  const isRestrictedPath = restrictedPaths.some(path => location.pathname.startsWith(path));

  // Check if user is Org Operator (exact match from your login response)
  const isOrgOperator = account.role?.name === 'Org Operator';

  const homePath = getHomePath(account.userId, account.role?.name);

  if (isOrgOperator && isRestrictedPath) {
    console.log('Redirecting Org Operator from restricted path:', location.pathname);
    return <Navigate to={homePath} replace />;
  }

  // Org Manager can access Devices, Property, Delivery, and Resident but not Settings
  const isOrgManager = account.role?.name === 'Org Manager';
  const managerRestrictedPaths = ['/settings', '/visitors', '/community', '/billing'];
  const isManagerRestrictedPath = managerRestrictedPaths.some(path =>
    location.pathname.startsWith(path)
  );

  if (isOrgManager && isManagerRestrictedPath) {
    console.log('Redirecting Org Manager from restricted path:', location.pathname);
    return <Navigate to={homePath} replace />;
  }

  // License-based restrictions - when at license limit
  if (isAtLimit) {
    const allowedWhenAtLimit = ['/dashboard', '/settings/license', '/profile', '/logout'];

    const isAllowedWhenAtLimit = allowedWhenAtLimit.some(path =>
      location.pathname.startsWith(path)
    );

    if (!isAllowedWhenAtLimit) {
      console.log('Redirecting due to license limit:', location.pathname);
      return <Navigate to="/dashboard" replace />;
    }
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}
